import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { createSlot } from '@radix-ui/react-slot';
import { RemoveScroll } from 'react-remove-scroll';

import { cn } from '../../lib/utils';
// Leaf import rather than the `shared/hooks` barrel — a UI primitive must not
// pull that whole graph in.
import { useSafeCollisionPadding } from '../../shared/hooks/useSafeAreaInsets';
import { POPOVER_CONTENT } from '../../shared/styles/animations';
import { useModalSurfaceContent } from './modal-scroll-lock';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

/** Host for the panel's own scroll lock — see `PopoverContent`. */
const PopoverContentSlot = createSlot('PopoverContent.RemoveScroll');

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, collisionPadding, ...props }, ref) => {
  // The panel is positioned against the VIEWPORT, which on the edge-to-edge
  // Android shell runs behind the navigation bar — so its collision detection
  // counts the strip behind that bar as usable space and settles a popover
  // opened low on the screen with its last rows untappable. `collisionPadding`
  // is a JS number, so it cannot read `var(--safe-bottom)`; the hook resolves
  // the inset to pixels and keeps a caller's own padding as a floor.
  //
  // Every inset is 0px on the web and on desktop, so this repositions nothing
  // there: it resolves to exactly the `collisionPadding` the caller passed, or
  // to Radix's own 0 default when it passed none.
  const safeCollisionPadding = useSafeCollisionPadding(collisionPadding);

  // The content node of the modal surface this panel is inside, or null on a
  // plain page. Non-null means a `react-remove-scroll` lock owns the document
  // and would swallow the wheel over this portaled panel, so we take a lock of
  // our own and shard the surface's body back in. Why not Radix's `modal`:
  // `modal-scroll-lock`.
  const surfaceContent = useModalSurfaceContent();
  const shards = React.useMemo(
    () => (surfaceContent ? [surfaceContent] : undefined),
    [surfaceContent],
  );

  return (
    <PopoverPrimitive.Portal>
      {/* Mirrors Radix's own idiom for this wrapper: a Slot so the lock adds no
          DOM node of its own and its wheel handlers land on the panel element.
          `removeScrollBar` stays off — the surface's lock already removed the
          page scrollbar, and a second removal would re-measure a gap of 0. */}
      <RemoveScroll
        as={PopoverContentSlot}
        enabled={surfaceContent !== null}
        removeScrollBar={false}
        allowPinchZoom
        shards={shards}
      >
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={safeCollisionPadding}
          className={cn(
            'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none',
            POPOVER_CONTENT,
            className
          )}
          {...props}
        />
      </RemoveScroll>
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
